import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { getFamily } from '../services/familyApi';
import { getNextTrip } from '../services/plannerApi';
import type { HomePoint, SurfaceWindow } from '../utils/sun';

/**
 * One theme: paper. There is no dark theme and no theme setting, so this
 * provider paints nothing; it supplies the sky inputs Today's daylight line
 * reads and the SUNSET tag preference.
 *
 * Older builds stored a theme choice under this key. It is deleted once on
 * mount and never read.
 */
export const LEGACY_THEME_KEY = 'vizday-theme';

/** The sky inputs re-read the clock once a minute (Almanac S2). */
export const SKY_TICK_MS = 60 * 1000;
/** `next-trip` and the home point are refetched every 30 minutes and on return to the page. */
export const SKY_REFETCH_MS = 30 * 60 * 1000;

function homePointOf(family: { home_latitude?: number | null; home_longitude?: number | null }): HomePoint | null {
  const { home_latitude: lat, home_longitude: lng } = family;
  return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null;
}

/**
 * What Today's daylight line reads: the clock, the home point and the trip
 * windows.
 *
 * Fetches for a signed-in account, keyed on that account so a switch never reads the previous account's home. The minute tick moves the
 * clock and nothing else: every window start and end is crossed by the tick
 * against the list already held, so no fetch or timer is scheduled at a stop
 * instant. A failed fetch keeps the last list and home point. The coordinates
 * stay in this component's state — never logged, stored or sent anywhere.
 */
function useSkyInputs(active: boolean, account: number | null) {
  const [now, setNow] = useState(() => new Date());
  const [home, setHome] = useState<HomePoint | null>(null);
  const [windows, setWindows] = useState<readonly SurfaceWindow[]>([]);

  // A different account (or none) never reads the last account's home or trips.
  useEffect(() => {
    setHome(null);
    setWindows([]);
  }, [account]);

  useEffect(() => {
    if (!active || account == null) return undefined;
    let live = true;
    const refresh = () => {
      setNow(new Date());
      getNextTrip()
        .then((payload) => {
          if (live && Array.isArray(payload?.surface_windows)) setWindows(payload.surface_windows);
        })
        .catch(() => {
          /* keep the last list */
        });
      getFamily()
        .then((family) => {
          if (live && family) setHome(homePointOf(family));
        })
        .catch(() => {
          /* keep the last home point */
        });
    };
    refresh();
    const tick = setInterval(() => setNow(new Date()), SKY_TICK_MS);
    const refetch = setInterval(refresh, SKY_REFETCH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      clearInterval(tick);
      clearInterval(refetch);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, account]);

  return { now, home, windows };
}

/** Sky inputs as the daylight line reads them (Almanac S3). */
export interface SkyInputs {
  now: Date;
  home: HomePoint | null;
  windows: readonly SurfaceWindow[];
}

/** The SUNSET tag preference (Almanac S3, Decision 6): on unless turned off. */
export const TAG_SUNSET_STORAGE_KEY = 'vizday-tag-sunset-stops';

function readStoredTagSunset(): boolean {
  try {
    return localStorage.getItem(TAG_SUNSET_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function removeLegacyTheme(): void {
  try {
    localStorage.removeItem(LEGACY_THEME_KEY);
  } catch {
    /* nothing to clean up without storage */
  }
}

interface ThemeContextType {
  /** Clock, home point and trip windows, signed-in only (empty signed out). */
  sky: SkyInputs;
  /** Whether itinerary rows carry the SUNSET tag (default on; toggled in Settings' Timetable row). */
  tagSunsetStops: boolean;
  setTagSunsetStops: (on: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const account = useUserStore((s) => s.user?.id ?? null);
  const signedIn = isAuthenticated && account != null;
  const [tagSunsetStops, setTagSunsetState] = useState<boolean>(readStoredTagSunset);
  const { now, home, windows } = useSkyInputs(signedIn, account);

  useEffect(() => {
    removeLegacyTheme();
  }, []);

  const setTagSunsetStops = (on: boolean) => {
    setTagSunsetState(on);
    try {
      localStorage.setItem(TAG_SUNSET_STORAGE_KEY, on ? 'true' : 'false');
    } catch {
      /* the choice still applies for this session */
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        sky: { now, home: signedIn ? home : null, windows: signedIn ? windows : [] },
        tagSunsetStops,
        setTagSunsetStops,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * The theme context, or `undefined` outside a provider. For components that
 * render in trees without one (their tests, embeds): they fall back to their
 * defaults instead of throwing.
 */
export function useOptionalTheme(): ThemeContextType | undefined {
  return useContext(ThemeContext);
}

export default ThemeContext;
